import { ArrowDown } from 'lucide-react'
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { Button } from '../../../ui/Button/Button'
import { Spinner } from '../../../ui/Spinner/Spinner'
import { StatePanel } from '../../../ui/StatePanel/StatePanel'
import type { ConversationMessage } from '../conversations-model'
import styles from '../ConversationsPage.module.css'
import { ConversationEmptyState } from './ConversationEmptyState'
import { MessageBubble } from './MessageBubble'

function dayKey(timestamp: number) {
  const date = new Date(timestamp)
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function dayLabel(timestamp: number) {
  const date = new Date(timestamp)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  }).format(date)
}

export function MessageList({
  status,
  messages,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  historyKey,
}: {
  status: 'idle' | 'loading' | 'ready' | 'forbidden' | 'error'
  messages: ConversationMessage[]
  hasOlder: boolean
  loadingOlder: boolean
  onLoadOlder: () => void
  historyKey: string | null
}) {
  const historyRef = useRef<HTMLDivElement>(null)
  const previousHistory = useRef<{
    key: string | null
    firstMessageId: string | null
    lastMessageId: string | null
    scrollHeight: number
  }>({
    key: null,
    firstMessageId: null,
    lastMessageId: null,
    scrollHeight: 0,
  })
  const followingLatest = useRef(true)
  const [showScrollToLatest, setShowScrollToLatest] = useState(false)

  const updateScrollPosition = useCallback(() => {
    const history = historyRef.current
    if (!history) return
    const distanceFromBottom =
      history.scrollHeight - history.scrollTop - history.clientHeight
    const isNearLatest = distanceFromBottom <= 64
    followingLatest.current = isNearLatest
    setShowScrollToLatest(!isNearLatest)
  }, [])

  useEffect(() => {
    setShowScrollToLatest(false)
    followingLatest.current = true
  }, [historyKey])

  useLayoutEffect(() => {
    const history = historyRef.current
    if (!history || status !== 'ready' || messages.length === 0) return
    const previous = previousHistory.current
    const firstMessageId = messages[0]?.messageId ?? null
    const lastMessageId = messages.at(-1)?.messageId ?? null
    const switchedHistory = previous.key !== historyKey
    const prependedOlderMessages =
      !switchedHistory &&
      previous.firstMessageId !== null &&
      previous.firstMessageId !== firstMessageId &&
      previous.lastMessageId === lastMessageId
    const receivedLatestMessage =
      !switchedHistory &&
      previous.lastMessageId !== null &&
      previous.lastMessageId !== lastMessageId

    if (switchedHistory || previous.lastMessageId === null) {
      history.scrollTop = history.scrollHeight
      followingLatest.current = true
    } else if (prependedOlderMessages) {
      history.scrollTop += history.scrollHeight - previous.scrollHeight
    } else if (receivedLatestMessage && followingLatest.current) {
      history.scrollTop = history.scrollHeight
    }

    previousHistory.current = {
      key: historyKey,
      firstMessageId,
      lastMessageId,
      scrollHeight: history.scrollHeight,
    }
    updateScrollPosition()
  }, [historyKey, messages, status, updateScrollPosition])

  useEffect(() => {
    const history = historyRef.current
    if (!history || status !== 'ready') return
    const update = () => updateScrollPosition()
    history.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    update()
    return () => {
      history.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [historyKey, status, updateScrollPosition])

  const scrollToLatest = () => {
    const history = historyRef.current
    if (!history) return
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches
    history.scrollTo({
      top: history.scrollHeight,
      behavior: reduceMotion ? 'auto' : 'smooth',
    })
    followingLatest.current = true
    setShowScrollToLatest(false)
  }

  if (status === 'loading') return <Spinner label="Loading messages" />
  if (status === 'forbidden') {
    return (
      <StatePanel
        kind="forbidden"
        title="Messages unavailable"
        description="You do not have permission to view this thread."
      />
    )
  }
  if (status === 'error') {
    return (
      <StatePanel
        kind="error"
        title="Messages unavailable"
        description="This message thread could not be loaded."
      />
    )
  }
  if (status !== 'ready' || messages.length === 0) {
    return (
      <ConversationEmptyState
        title="No displayable messages"
        description="This thread has no timestamped message content to show."
      />
    )
  }
  return (
    <div className={styles.messageViewport}>
      <div
        ref={historyRef}
        className={styles.messageHistory}
        aria-label="SMS message history viewport"
      >
        {hasOlder ? (
          <Button
            className={styles.olderMessages}
            variant="secondary"
            type="button"
            disabled={loadingOlder}
            onClick={onLoadOlder}
          >
            {loadingOlder ? 'Loading…' : 'Load older messages'}
          </Button>
        ) : null}
        <ol className={styles.messageList} aria-label="Message history">
          {messages.map((message, index) => {
            const previous = messages[index - 1]
            const startsDay =
              !previous ||
              dayKey(previous.timestamp) !== dayKey(message.timestamp)
            return (
              <Fragment key={message.messageId}>
                {startsDay ? (
                  <li className={styles.daySeparator}>
                    <span>{dayLabel(message.timestamp)}</span>
                  </li>
                ) : null}
                <MessageBubble message={message} />
              </Fragment>
            )
          })}
        </ol>
      </div>
      {showScrollToLatest ? (
        <button
          className={styles.scrollToLatest}
          type="button"
          aria-label="Scroll to latest message"
          onClick={scrollToLatest}
        >
          <ArrowDown size={17} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}
